import { services } from '@ambita/ambita-components-core';
import { TagInterface } from 'riot';
import * as cadastreReportConstants from '../../constants/cadastreReport';
import cadastreReportStore from '../../stores/cadastreReport';
import { i18nMixin, I18nMixin } from '../i18nMixin';

const { events: cadastreReportEvents } = cadastreReportConstants;

export interface CadastreReportSummaryTag extends TagInterface, I18nMixin {
  opts: {
    productName: string;
    propertyAddressKey: string;
    summaryLinkHandler(link: string): void;
  };
  lookupData: any;
  loading: boolean;
  getShortStreetAddress(): string;
  getPostalLocation(): string;
  count(item: string): number;
  countOwners(): number;
  countUnregisteredOwnerships(): number;
  countAddresses(): number;
  isCadastreReport(): boolean;
  isCadastreInfo(): boolean;
  onSummaryLinkClick(event: Event): void;
}

export const init = (tag: CadastreReportSummaryTag): void => {
  tag.mixin(i18nMixin);
  tag.lookupData = {};

  const countInItems = (item: string): number => {
    return tag.lookupData.data[item] ? tag.lookupData.data[item].length : 0;
  };

  const countInMainCadastreItems = (item: string): number => {
    return tag.lookupData.data.mainCadastre && tag.lookupData.data.mainCadastre[item] ?
      tag.lookupData.data.mainCadastre[item].length : 0;
  };

  const onCadastreLookupLoading = (): void => {
    tag.lookupData = null;
    tag.loading = true;
    tag.update();
  };

  const onCadastreLookupLoaded = (): void => {
    tag.lookupData = cadastreReportStore.getState();
    tag.loading = false;
    tag.update();
  };

  tag.getShortStreetAddress = (): string => {
    const addresses = tag.lookupData.data.addresses;

    if (!addresses || addresses.length < 1) {
      return '';
    }

    const addressKey = tag.opts.propertyAddressKey;

    if (!addressKey) {
      return addresses[0].shortStreetAddress || '';
    }

    return addresses.filter(address => address.addressKey === addressKey)[0].shortStreetAddress || '';
  };

  tag.getPostalLocation = (): string => {
    return tag.lookupData.data.addresses && tag.lookupData.data.addresses[0].postalLocation || '';
  };

  tag.count = (item: string): number => {
    return countInItems(item);
  };

  tag.countUnregisteredOwnerships = (): number => {
    const unregisteredOwnershipsCount = countInItems('unregisteredOwnerships');
    const mainCadastreUnregisteredOwnershipsCount = countInMainCadastreItems('unregisteredOwnerships');
    const unregisteredCadastreOwnershipsCount = countInItems('unregisteredCadastreOwnerships');
    const mainCadastreUnregisteredCadastreOwnershipsCount = countInMainCadastreItems('unregisteredCadastreOwnerships');

    return unregisteredOwnershipsCount + mainCadastreUnregisteredOwnershipsCount + unregisteredCadastreOwnershipsCount +
      mainCadastreUnregisteredCadastreOwnershipsCount;
  };

  tag.countOwners = (): number => {
    const ownersCount = countInItems('owners');
    const mainCadastreOwnersCount = countInMainCadastreItems('owners');
    const unregisteredOwnershipsCount = tag.countUnregisteredOwnerships();

    return ownersCount + mainCadastreOwnersCount + unregisteredOwnershipsCount;
  };

  tag.countAddresses = (): number => {
    let addressesCount = 0;

    if (tag.lookupData.data.districts) {
      tag.lookupData.data.districts.forEach((district) => {
        addressesCount = addressesCount + district.addresses.length;
      });
    }

    return addressesCount;
  };

  tag.isCadastreReport = (): boolean => {
    return tag.opts.productName && tag.opts.productName === 'cadastreReport';
  };

  tag.isCadastreInfo = (): boolean => {
    return tag.opts.productName && tag.opts.productName === 'cadastreInfo';
  };

  tag.onSummaryLinkClick = (event: Event): void => {
    if (typeof tag.opts.summaryLinkHandler !== 'function') {
      return;
    }

    const target = event.target as HTMLElement;

    if (services.domUtils.classUtils.hasClass(target, 'ambita-report-link')) {
      event.preventDefault();
      tag.opts.summaryLinkHandler(target.getAttribute('href'));
    }
  };

  tag.on('mount', () => {
    cadastreReportStore.on(cadastreReportEvents.CADASTRE_LOOKUP_LOADING, onCadastreLookupLoading);
    cadastreReportStore.on(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, onCadastreLookupLoaded);
  });

  tag.on('unmount', () => {
    cadastreReportStore.off(cadastreReportEvents.CADASTRE_LOOKUP_LOADING, onCadastreLookupLoading);
    cadastreReportStore.off(cadastreReportEvents.CADASTRE_LOOKUP_LOADED, onCadastreLookupLoaded);
  });
};
